import React, { useEffect, useState } from "react";
import { Share, PlusSquare, Download, Smartphone, Apple, Chrome as ChromeIcon, Check } from "lucide-react";

/**
 * Visible, permanent "Install to home screen" section on the landing page.
 *
 * Unlike the transient InstallPrompt toast (which appears after 45 s), this
 * is a large card users can scroll to any time. Two device-specific columns:
 *   • iOS Safari — visual Share → Add to Home Screen instructions
 *   • Android Chrome / Edge — one-tap Install button via beforeinstallprompt
 *
 * Detects standalone mode and swaps in a warm "You're installed" state.
 */
function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallHomescreen() {
  const [installEvent, setInstallEvent] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [status, setStatus] = useState(null); // 'accepted' | 'dismissed'

  useEffect(() => {
    setInstalled(isStandalone());
    const onBIP = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setStatus("accepted");
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    const result = await installEvent.userChoice.catch(() => ({ outcome: "dismissed" }));
    setStatus(result.outcome);
    if (result.outcome === "accepted") {
      setInstalled(true);
    }
    setInstallEvent(null);
  };

  return (
    <section
      id="install-app"
      data-testid="install-homescreen"
      className="relative border-t border-[color:var(--gold)]/10"
    >
      <div className="mx-auto max-w-[1300px] px-6 md:px-12 py-16 md:py-24">
        <div className="max-w-3xl mb-10 md:mb-14">
          <div className="label-overline text-[color:var(--gold)]">Install the app</div>
          <h2 className="mt-3 font-serif-display text-[color:var(--parchment)] text-3xl md:text-5xl leading-[1.05] tracking-tight">
            Keep Earth Pulse <span className="font-serif-italic text-[color:var(--gold)]">on your home screen</span>.
          </h2>
          <p className="mt-4 md:mt-5 text-[color:var(--parchment-soft)] text-base md:text-lg font-sans-body leading-relaxed">
            No app store needed. One tap and it opens like a native app — full-screen, offline-ready,
            with today&apos;s Schumann and moon phase a heartbeat away.
          </p>
        </div>

        {installed ? (
          <div
            className="rounded-2xl border border-[color:var(--gold)]/40 p-6 md:p-8 flex items-center gap-4"
            style={{ background: "rgba(212, 162, 76, 0.06)" }}
            data-testid="install-installed-state"
          >
            <div className="w-12 h-12 rounded-full bg-[color:var(--gold)] flex items-center justify-center">
              <Check size={22} className="text-[color:var(--bg-base)]" />
            </div>
            <div>
              <div className="font-serif-display text-[color:var(--parchment)] text-xl">You&apos;re installed ✨</div>
              <div className="font-serif-italic text-[color:var(--text-secondary)] text-sm mt-1">
                You&apos;re opening Earth Pulse from your home screen right now.
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* ── iOS Safari card ────────────────────────────────────── */}
            <div
              className="rounded-2xl border border-[color:var(--gold)]/25 p-6 md:p-8"
              style={{ background: "rgba(10, 17, 40, 0.55)" }}
              data-testid="install-ios-card"
            >
              <div className="flex items-center gap-2">
                <Apple size={16} className="text-[color:var(--parchment)]" />
                <span className="label-overline text-[color:var(--parchment)]">iPhone · iPad</span>
              </div>
              <h3 className="mt-3 font-serif-display text-[color:var(--parchment)] text-2xl">Safari</h3>
              <ol className="mt-5 space-y-4">
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 w-6 h-6 rounded-full border border-[color:var(--gold)]/50 flex items-center justify-center text-xs text-[color:var(--gold)] font-medium flex-shrink-0">
                    1
                  </span>
                  <span className="text-[color:var(--parchment-soft)] text-sm md:text-base leading-relaxed">
                    Tap the <Share size={14} className="inline text-[color:var(--gold)] mx-1" />{" "}
                    <span className="text-[color:var(--gold)]">Share</span> button at the bottom of Safari.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 w-6 h-6 rounded-full border border-[color:var(--gold)]/50 flex items-center justify-center text-xs text-[color:var(--gold)] font-medium flex-shrink-0">
                    2
                  </span>
                  <span className="text-[color:var(--parchment-soft)] text-sm md:text-base leading-relaxed">
                    Scroll down, then tap{" "}
                    <PlusSquare size={14} className="inline text-[color:var(--gold)] mx-1" />{" "}
                    <span className="text-[color:var(--gold)]">Add to Home Screen</span>.
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-0.5 w-6 h-6 rounded-full border border-[color:var(--gold)]/50 flex items-center justify-center text-xs text-[color:var(--gold)] font-medium flex-shrink-0">
                    3
                  </span>
                  <span className="text-[color:var(--parchment-soft)] text-sm md:text-base leading-relaxed">
                    Tap <span className="text-[color:var(--gold)]">Add</span>. Earth Pulse will appear
                    on your home screen with the crescent-moon icon.
                  </span>
                </li>
              </ol>
              <p className="mt-6 font-serif-italic text-[color:var(--text-muted)] text-xs">
                Must be opened in Safari for this to work — Chrome on iPhone doesn&apos;t support it yet.
              </p>
            </div>

            {/* ── Android / desktop Chrome card ─────────────────────── */}
            <div
              className="rounded-2xl border border-[color:var(--gold)]/25 p-6 md:p-8"
              style={{ background: "rgba(10, 17, 40, 0.55)" }}
              data-testid="install-android-card"
            >
              <div className="flex items-center gap-2">
                <ChromeIcon size={16} className="text-[color:var(--parchment)]" />
                <span className="label-overline text-[color:var(--parchment)]">Android · desktop</span>
              </div>
              <h3 className="mt-3 font-serif-display text-[color:var(--parchment)] text-2xl">
                Chrome · Edge · Samsung
              </h3>
              {installEvent ? (
                <div className="mt-5">
                  <p className="text-[color:var(--parchment-soft)] text-sm md:text-base font-sans-body leading-relaxed">
                    Tap the button below — your browser will ask once, and then Earth Pulse will land on your home screen.
                  </p>
                  <button
                    type="button"
                    onClick={install}
                    data-testid="install-android-button"
                    className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium text-base"
                  >
                    <Download size={16} /> Install Earth Pulse
                  </button>
                  {status === "dismissed" && (
                    <div className="mt-4 font-serif-italic text-[color:var(--text-muted)] text-xs">
                      No worries — tap the button any time you&apos;re ready.
                    </div>
                  )}
                </div>
              ) : (
                <ol className="mt-5 space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 w-6 h-6 rounded-full border border-[color:var(--gold)]/50 flex items-center justify-center text-xs text-[color:var(--gold)] font-medium flex-shrink-0">
                      1
                    </span>
                    <span className="text-[color:var(--parchment-soft)] text-sm md:text-base leading-relaxed">
                      Open the browser menu{" "}
                      <span className="text-[color:var(--gold)]">(⋮ or ⋯)</span>.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 w-6 h-6 rounded-full border border-[color:var(--gold)]/50 flex items-center justify-center text-xs text-[color:var(--gold)] font-medium flex-shrink-0">
                      2
                    </span>
                    <span className="text-[color:var(--parchment-soft)] text-sm md:text-base leading-relaxed">
                      Tap{" "}
                      <span className="text-[color:var(--gold)]">Install app</span>{" "}
                      or{" "}
                      <span className="text-[color:var(--gold)]">Add to Home screen</span>.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="mt-0.5 w-6 h-6 rounded-full border border-[color:var(--gold)]/50 flex items-center justify-center text-xs text-[color:var(--gold)] font-medium flex-shrink-0">
                      3
                    </span>
                    <span className="text-[color:var(--parchment-soft)] text-sm md:text-base leading-relaxed">
                      Confirm the name — Earth Pulse will open like a native app from now on.
                    </span>
                  </li>
                </ol>
              )}
              <p className="mt-6 font-serif-italic text-[color:var(--text-muted)] text-xs">
                Works fully offline for the last-opened pages. Live data refreshes as soon as you&apos;re back online.
              </p>
            </div>
          </div>
        )}

        {/* Feature strip */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Home-screen icon", note: "always one tap away" },
            { label: "Full-screen", note: "no browser bars" },
            { label: "Offline shell", note: "opens even without signal" },
            { label: "Free forever", note: "no app store fees" },
          ].map((f) => (
            <div key={f.label} className="text-center md:text-left">
              <div className="w-8 h-8 mx-auto md:mx-0 rounded-full border border-[color:var(--gold)]/40 flex items-center justify-center">
                <Smartphone size={14} className="text-[color:var(--gold)]" />
              </div>
              <div className="mt-3 font-serif-display text-[color:var(--parchment)] text-sm md:text-base">{f.label}</div>
              <div className="font-serif-italic text-[color:var(--text-secondary)] text-xs mt-1">{f.note}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
